import type { MapData, Level, Category, Location, CategoriesMap, LocationsMap, MapDataOptions } from '../types';


function parseNum(val: unknown): number {
  return typeof val === 'string' ? parseFloat(val) : Number(val);
}


function parseLocation(loc: unknown, levelId: string): Location {
  if (typeof loc !== 'object' || loc === null) {
    throw new Error('Invalid location data: expected an object');
  }

  return {
    id: ('id' in loc) ? String(loc.id) : '',
    title: ('title' in loc) ? String(loc.title) : '',
    description: ('description' in loc) ? String(loc.description) : null,
    about: ('about' in loc) ? String(loc.about) : null,
    pin: ('pin' in loc) ? String(loc.pin) : null,
    category: ('category' in loc) ? (Array.isArray(loc.category) ? loc.category.map(String) : [String(loc.category)]) : [],
    image: ('image' in loc) ? String(loc.image) : null,
    link: ('link' in loc) ? String(loc.link) : null,
    fill: ('fill' in loc) ? String(loc.fill) : null,
    x: ('x' in loc) ? parseNum(loc.x) : 0,
    y: ('y' in loc) ? parseNum(loc.y) : 0,
    listElem: null,
    elem: null,
    level: levelId,
  };
}

function parseLevelsAndLocations(levels: unknown): { levels: Level[]; locations: LocationsMap } {
  if (!Array.isArray(levels)) {
    throw new Error('Invalid levels data: expected an array');
  }
  if (levels.length === 0) {
    throw new Error('No levels found in map data');
  }

  const locations: LocationsMap = {};

  // reverse levels to match Mapplic's rendering order (last level in array is bottom layer)
  const parsedLevels = levels.map(level => {
    if (typeof level !== 'object' || level === null) {
      throw new Error('Invalid level data: expected an object');
    }

    if (!('id' in level) || !('title' in level) || !('map' in level) || !('locations' in level)) {
      throw new Error('Level data missing required fields: id, title, map, locations');
    }

    const levelId = String(level.id);

    // Parse locations for this level into the flat map
    if (!Array.isArray(level.locations)) {
      throw new Error('Invalid locations data: expected an array');
    }
    for (const loc of level.locations) {
      const parsed = parseLocation(loc, levelId);
      locations[parsed.id] = parsed;
    }

    return {
      id: levelId,
      title: String(level.title),
      map: String(level.map),
      show: ('show' in level) ? !!level.show : false,
    };
  }).slice().reverse();

  return { levels: parsedLevels, locations };
}

function parseCategories(categories: unknown): CategoriesMap {
  if (!Array.isArray(categories)) {
    throw new Error('Invalid categories data: expected an array');
  }

  const map: CategoriesMap = {};
  for (const cat of categories) {
    const parsed = parseCategory(cat);
    map[parsed.id] = parsed;
  }
  return map;
}

function parseCategory(cat: unknown): Category {
  if (typeof cat !== 'object' || cat === null) {
    throw new Error('Invalid category data: expected an object');
  }

  if (!('id' in cat) || !('title' in cat) || !('color' in cat)) {
    throw new Error('Category data missing required fields: id, title, color');
  }

  return {
    id: String(cat.id),
    title: String(cat.title),
    color: String(cat.color),
    numberOfLocations: 0,
  };
}

function parseOptions(data: unknown): MapDataOptions {
  if (typeof data !== 'object' || data === null) {
    throw new Error('Invalid map data: expected an object');
  }
  return {
    zoomMargin: ('zoommargin' in data) ? parseNum(data.zoommargin) : null,
    zoomSteps: ('zoomsteps' in data) ? parseNum(data.zoomsteps) : null,
    maxScale: ('maxscale' in data) ? parseNum(data.maxscale) : null,
  };

}

export function parseMapData(raw: unknown): MapData {
  if (typeof raw !== 'object' || raw === null) {
    throw new Error('Invalid map data: expected an object');
  }

  if (!('mapwidth' in raw) || !('mapheight' in raw) || !('levels' in raw)) {
    throw new Error('Map data missing required fields: mapwidth, mapheight, levels');
  }

  const { levels, locations } = parseLevelsAndLocations(raw.levels);
  const categories = parseCategories(('categories' in raw) ? raw.categories : []);

  // Count locations per category
  for (const loc of Object.values(locations)) {
    for (const catId of loc.category) {
      const cat = categories[catId];
      if (cat) {
        cat.numberOfLocations++;
      }
    }
  }

  return {
    mapwidth: parseNum(raw.mapwidth),
    mapheight: parseNum(raw.mapheight),
    options: parseOptions(raw),
    levels,
    categories,
    locations,
  };
}
